import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import {Button, Link} from '@primer/react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {announce} from '@github-ui/aria-live'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export interface CopilotBudgetRequestBannerAnalytics {
  visibleEvent: string
  dismissEvent: string
  clickEvent: string
  location: string
}

export type BannerState = 'pre_request' | 'post_request' | 'request_denied'

export interface CopilotBudgetRequestBannerProps {
  isPersonalBudget: boolean
  isEnterprise?: boolean
  organizationName: string
  enterpriseName?: string
  resetDate: string
  requestUrl: string
  requestedAmountCents: number
  removeRequestUrl?: string
  contactUrl: string
  dismissPaths: Record<BannerState, string>
  bannerState: BannerState
  declinedDate?: string
  analytics: CopilotBudgetRequestBannerAnalytics
  rounded?: boolean
}

// Append the interaction's page + element to a URL's query string. They ride on the query
// (not a JSON body) because the backend reads params[:display_location]/[:surface] in its
// instrumentation around-action, before parse_json_params merges the request's JSON body.
function appendInteractionTags(url: string, tags: IpmMetricTags): string {
  const query = new URLSearchParams()
  if (tags.display_location) query.set('display_location', tags.display_location)
  if (tags.surface) query.set('surface', tags.surface)
  const queryString = query.toString()
  return queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url
}

export function CopilotBudgetRequestBanner({
  isPersonalBudget,
  isEnterprise,
  organizationName,
  enterpriseName,
  resetDate,
  requestUrl,
  requestedAmountCents,
  removeRequestUrl,
  contactUrl,
  dismissPaths,
  bannerState: initialBannerState,
  declinedDate,
  analytics,
  rounded,
}: CopilotBudgetRequestBannerProps) {
  const [bannerState, setBannerState] = useState<BannerState>(initialBannerState)
  const [submitting, setSubmitting] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)
  const isInitialRenderRef = useRef(true)
  const dismissPath = dismissPaths[bannerState]
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'CopilotBudgetRequestBanner',
      surface: 'copilot_budget_request_banner',
      display_location: analytics.location,
      category: 'copilot_budget_request_banner',
      nudge_id: 'copilot_premium_request_budget',
      variant: bannerState,
    }),
    [analytics.location, bannerState],
  )

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }
    bannerRef.current?.focus()
  }, [bannerState])

  const handleRequestClick = useCallback(async () => {
    reportIpmAction('cta_click', telemetryTags)
    setSubmitting(true)
    try {
      const response = await verifiedFetch(appendInteractionTags(requestUrl, telemetryTags), {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({requested_amount_cents: requestedAmountCents}),
      })
      if (response.ok) {
        setBannerState('post_request')
        announce(
          isEnterprise
            ? 'Request sent. Your enterprise account owners will be notified.'
            : 'Request sent. Your org admins will be notified.',
        )
      }
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Network error — keep current state
    } finally {
      setSubmitting(false)
    }
  }, [requestUrl, requestedAmountCents, telemetryTags, isEnterprise])

  const handleRemoveRequestClick = useCallback(async () => {
    if (!removeRequestUrl) return
    reportIpmAction('cta_click', telemetryTags)
    setSubmitting(true)
    try {
      const response = await verifiedFetch(appendInteractionTags(removeRequestUrl, telemetryTags), {method: 'DELETE'})
      if (response.ok) {
        setBannerState('pre_request')
        announce('Request removed.')
      }
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Network error — keep current state
    } finally {
      setSubmitting(false)
    }
  }, [removeRequestUrl, telemetryTags])

  // Shared follow-up terminology so the post_request and request_denied states stay consistent
  // with the pre_request copy: per-user budgets say "AI credits", org pools say "Copilot budget";
  // enterprise-scoped budgets point at account owners instead of org admins.
  const budgetTerm = isPersonalBudget ? 'AI credits' : 'Copilot budget'
  const approvers = isEnterprise ? 'enterprise account owners' : 'org admins'
  const approver = isEnterprise ? 'an enterprise account owner' : 'an admin'

  if (bannerState === 'post_request') {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="Request sent"
        variant="success"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            <strong>Request sent.</strong>
            {` Your ${approvers} will be notified that you need additional ${budgetTerm}.`}
            {removeRequestUrl && (
              <>
                <br />
                <span className="d-inline-flex flex-items-center mt-1">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleRemoveRequestClick}
                    disabled={submitting}
                    data-analytics-event={analytics.clickEvent}
                  >
                    Remove request
                  </Button>
                  <span className="ml-2">You can re-request after this is reviewed.</span>
                </span>
              </>
            )}
          </span>
        }
      />
    )
  }

  if (bannerState === 'request_denied') {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="Budget request declined"
        variant="warning"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            {declinedDate ? (
              <span>
                {`Your request for additional ${budgetTerm} was declined by ${approver} on `}
                <strong>{declinedDate}</strong>
                {'. '}
              </span>
            ) : (
              <span>{`Your request for additional ${budgetTerm} was declined by ${approver}. `}</span>
            )}
            <Link
              href={contactUrl}
              inline
              className="Link--inTextBlock"
              onClick={() => reportIpmAction('click', telemetryTags)}
            >
              {`Contact ${approver}`}
            </Link>{' '}
            <span>for more details.</span>
          </span>
        }
      />
    )
  }

  // pre_request state
  if (isPersonalBudget) {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="AI credits exhausted"
        variant="critical"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            {isEnterprise ? (
              <>
                You&apos;ve used all your AI credits from <strong>{enterpriseName || organizationName}</strong>. Access
                resumes on {resetDate}. Your <strong>enterprise account owners</strong> can increase it.
              </>
            ) : (
              <>
                You&apos;ve used all your AI credits from <strong>{organizationName}</strong>. Access resumes on{' '}
                {resetDate}. Your org admins can increase it sooner.
              </>
            )}
          </span>
        }
        primaryAction={
          <Button onClick={handleRequestClick} disabled={submitting} data-analytics-event={analytics.clickEvent}>
            {submitting ? 'Submitting...' : 'Request more budget'}
          </Button>
        }
      />
    )
  }

  return (
    <IpmBannerBase
      ref={bannerRef}
      dismissPath={dismissPath}
      visibleEvent={analytics.visibleEvent}
      dismissEvent={analytics.dismissEvent}
      testId="copilot-budget-request-banner"
      title="Copilot budget exhausted"
      variant="critical"
      location={analytics.location}
      rounded={rounded}
      telemetryTags={telemetryTags}
      description={
        <span className="text-normal">
          Your organization <strong>{organizationName}</strong> has used its full Copilot budget. Access resumes on{' '}
          {resetDate}. Your org admins can increase it sooner.
        </span>
      }
      primaryAction={
        <Button onClick={handleRequestClick} disabled={submitting} data-analytics-event={analytics.clickEvent}>
          {submitting ? 'Submitting...' : 'Request additional budget'}
        </Button>
      }
    />
  )
}
